import { RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermission } from '../../../auth/AuthContext';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { IndexPage } from '../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../components/index-page/types';
import type { MenuItem } from '../../../components/Menu';
import { StatusText } from '../../../components/table/StatusText';
import { TextCell } from '../../../components/table/TextCell';
import { ApiError } from '../../../lib/apiError';
import { formatMoney } from '../../../lib/formatMoney';
import { usePersonNames, type PersonNames } from '../../human-resources/personnel/usePersonNames';
import { cashCatalogsApi } from '../cashCatalogsApi';
import { DisbursementRequestModal } from '../components/DisbursementRequestModal';
import { disbursementRequestsApi } from '../disbursementRequestsApi';
import { DR_STATUS, type DisbursementRequestListItem } from '../disbursementRequestTypes';

/**
 * Cash Division › Disbursements — every request to the Cash Division, filed by where it is in the
 * route: Pending → At Accounting → For release → Disbursed.
 *
 * <b>The requesting party's NAME is resolved for the page on screen</b>, in one batch through
 * the identity service — a row carries a personId and no name. The column is gated on
 * `personnel.read`, the key the resolve needs, and it can be neither searched nor sorted: the
 * names live in another database.
 */
export function DisbursementRequestsIndexPage() {
  const navigate = useNavigate();
  const canReadPersonnel = usePermission('personnel.read');

  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'delete' | 'restore';
    row: DisbursementRequestListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [pageIds, setPageIds] = useState<string[]>([]);
  const names = usePersonNames(canReadPersonnel ? pageIds : []);

  /*
    WHAT A REQUEST CANNOT BE RAISED WITHOUT — one transaction type and one payment type, both
    required pickers on the form. Seeded TRUE, and a failed feed leaves it true: a request that
    did not come back is not evidence that a list is empty.
  */
  const [hasTransactionType, setHasTransactionType] = useState(true);
  const [hasPaymentType, setHasPaymentType] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    cashCatalogsApi
      .options('transaction-types', controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setHasTransactionType(rows.length > 0);
      })
      .catch(() => undefined);

    cashCatalogsApi
      .options('payment-types', controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setHasPaymentType(rows.length > 0);
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [reloadKey]);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchRows = useCallback(async (query: IndexQueryState, signal: AbortSignal) => {
    const page = await disbursementRequestsApi.list(
      {
        search: query.search,
        page: query.page,
        pageSize: query.pageSize,
        sort: query.sort ?? undefined,
        status: query.facets.status?.join(','),
        stage: query.facets.stage?.join(','),
        transactionType: query.facets.transactionType?.join(','),
        paymentType: query.facets.paymentType?.join(','),
      },
      signal,
    );

    // Only the ids that came back; `!= null` because an absent one is omitted from the wire.
    setPageIds(page.items.map((r) => r.requesterPersonId).filter((id): id is string => id != null));

    return page;
  }, []);

  const columns = useMemo(() => buildColumns(names), [names]);

  const rowActions = useCallback(
    (row: DisbursementRequestListItem): MenuItem[] => [
      {
        key: `restore-${row.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'disbursement-requests.delete',
        disabledReason: row.isActive ? 'This request is already active.' : null,
        onClick: () => setConfirming({ kind: 'restore', row }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'delete') await disbursementRequestsApi.remove(confirming.row.id);
      else await disbursementRequestsApi.restore(confirming.row.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<DisbursementRequestListItem>
        module="disbursement-requests"
        breadcrumb={['Cash Division', 'Disbursements']}
        columns={columns}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'stage', label: 'Stage' },
          { key: 'transactionType', label: 'Transaction type' },
          { key: 'paymentType', label: 'Payment type' },
        ]}
        // Filed by where a request is in the route — the question a Cash clerk opens this with.
        folderNavFacet="stage"
        auditEntityTypes={['DisbursementRequest', 'DisbursementRequestItem']}
        rowKey={(row) => row.id}
        fetch={fetchRows}
        reloadKey={reloadKey}
        searchPlaceholder="Search by request number, particulars, voucher or payment reference"
        emptyMessage="No request yet. Raise one when an employee needs a disbursement."
        prerequisites={[
          {
            label: 'transaction types',
            met: hasTransactionType,
            to: '/cash-division/settings/transaction-types',
            permission: 'cash-catalogs.read',
          },
          {
            label: 'payment types',
            met: hasPaymentType,
            to: '/cash-division/settings/payment-types',
            permission: 'cash-catalogs.read',
          },
        ]}
        onCreate={() => setCreating(true)}
        createPermission="disbursement-requests.create"
        onRowOpen={(row) => navigate(`/cash-division/requests/${row.id}`)}
        openPermission="disbursement-requests.read"
        rowActions={rowActions}
        onDelete={(row) => setConfirming({ kind: 'delete', row })}
        deletePermission="disbursement-requests.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(row) =>
          !row.isActive
            ? 'This request is already deleted.'
            : row.status === DR_STATUS.disbursed
              ? 'A disbursed request is the record of the payment and cannot be deleted.'
              : null
        }
        otherInfo={(row) => row}
      />

      {creating ? (
        <DisbursementRequestModal
          open
          editing={null}
          onClose={() => setCreating(false)}
          onSaved={(saved) => {
            setCreating(false);
            navigate(`/cash-division/requests/${saved.id}`);
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'delete' ? 'Delete request' : 'Restore request'}
          danger={confirming.kind === 'delete'}
          requireConfirm={confirming.kind === 'delete'}
          requireCredential={confirming.kind === 'delete'}
          loading={running}
          id={`disbursement-request-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              {confirming.kind === 'delete' ? (
                <>
                  <p>{confirming.row.requestNumber} is hidden from the register.</p>
                  <p>
                    <strong>This is not the same as returning it.</strong> Deleting is for a request
                    keyed against the wrong employee, or twice. If the request cannot go forward,
                    return it from its own page — that is an outcome the register keeps.
                  </p>
                  <p>Nothing is erased — a deleted request can be restored from this list.</p>
                </>
              ) : (
                <p>{confirming.row.requestNumber} returns to the register where it was in the route.</p>
              )}

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

/** Built per render because the requester column reads the resolved names. */
function buildColumns(names: PersonNames): ColumnDef<DisbursementRequestListItem>[] {
  return [
    {
      key: 'requestNumber',
      header: 'Request',
      width: 340,
      sortable: true,
      render: (row) => <TextCell code={row.requestNumber} value={row.particulars} clamp />,
    },
    {
      key: 'requester',
      header: 'Requesting party',
      width: 220,
      summary: true,
      permission: 'personnel.read',
      render: (row) => (
        <TextCell
          value={
            row.requesterPersonId == null
              ? 'Restricted'
              : names.statusOf(row.requesterPersonId) === 'erased'
                ? 'Identity erased'
                : (names.nameOf(row.requesterPersonId) ?? (names.failed ? 'Name unavailable' : null))
          }
        />
      ),
    },
    {
      key: 'transactionType',
      header: 'Transaction type',
      width: 180,
      summary: true,
      render: (row) => <TextCell value={row.transactionTypeName} />,
    },
    {
      key: 'paymentType',
      header: 'Payment type',
      width: 150,
      render: (row) => <TextCell value={row.paymentTypeName} />,
    },
    {
      key: 'amount',
      header: 'Amount',
      width: 140,
      align: 'right',
      sortable: true,
      summary: true,
      render: (row) => <TextCell value={formatMoney(row.amount)} mono numeric />,
    },
    {
      key: 'voucherNumber',
      header: 'Voucher no.',
      width: 150,
      render: (row) => <TextCell value={row.voucherNumber} mono />,
    },
    {
      key: 'stage',
      header: 'Stage',
      width: 160,
      sortable: true,
      stage: true,
      render: (row) => <StatusText tone="inactive">{row.statusLabel}</StatusText>,
    },
    {
      key: 'status',
      header: 'Status',
      width: 120,
      status: true,
      render: (row) =>
        row.isActive ? (
          <StatusText tone="active">Active</StatusText>
        ) : (
          <StatusText tone="inactive">Deleted</StatusText>
        ),
    },
  ];
}
